import {ButtonProps, closeCurrentDialog, DataStatus, openDialog} from 'platform/components';
import {Box, HStack, VStack} from 'platform/foundation';

import {equals, isNil, not} from 'ramda';
import {isArray, isNilOrEmpty} from 'ramda-adjunct';

import {useGetWarehousesQuery} from '@dms/api/warehouse';
import {
  PostBasketMaterialItemPriceListApiApiArg,
  PostBasketMaterialItemWarehouseArticleApiApiArg,
  useDeleteBasketMaterialItemsApiMutation,
  useGetBasketMaterialItemsApiQuery,
  usePatchBasketItemMaterialApiMutation,
  usePatchBasketItemQuantityApiMutation,
  usePostBasketItemBulkDeleteMutation,
  usePostBasketMaterialItemCheckoutApiMutation,
  usePostBasketMaterialItemPriceListApiMutation,
  usePostBasketMaterialItemWarehouseArticleApiMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  AfterSalesMaterialBasket,
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  MaterialBasketItemEditingDetails,
  useBasketItemsQuantityState,
} from '@dms/shared';

import {buildArray, suffixTestId, TestIdProps} from 'shared/web';

import {ExtendedMateriallBasketItemType} from '../types/ExtendedMateriallBasketItemType';
import {EditMaterialItem} from './EditMaterialItem';
import {MaterialItemsListCard} from './MaterialItemsListCard';

interface AddMaterialItemsDialogProps extends TestIdProps {
  packageId: string;
  onClose: () => void;
  onAfterSubmit: () => void;
}

export function AddMaterialItemsDialog(props: AddMaterialItemsDialogProps) {
  const [postBasketMaterialItemCheckout, {isLoading: isCheckingOutBasket}] =
    usePostBasketMaterialItemCheckoutApiMutation();

  const [deleteBasketMaterialItems, {isLoading: isDiscardingBasket}] =
    useDeleteBasketMaterialItemsApiMutation();

  const [deleteBasketItems, {isLoading: isDeletingBasketItems}] =
    usePostBasketItemBulkDeleteMutation();

  const [patchBasketItemQuantity, {isLoading: isChangingQuantity}] =
    usePatchBasketItemQuantityApiMutation();

  const [postBasketMaterialItemPriceList, priceListItemResult] =
    usePostBasketMaterialItemPriceListApiMutation();

  const [postBasketMaterialItemWarehouseArticle, warehouseArticleItemResult] =
    usePostBasketMaterialItemWarehouseArticleApiMutation();

  const [, {isLoading: isEditingMaterialitem}] = usePatchBasketItemMaterialApiMutation({
    fixedCacheKey: 'edit-basket-material-item',
  });

  const {
    data: availableWarehouses,
    isLoading: isAvailableWarehousesLoading,
    isError: isAvailableWarehousesError,
  } = useGetWarehousesQuery({});

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: isBasketError,
  } = useGetBasketMaterialItemsApiQuery({
    packageId: props.packageId,
  });

  const basketItems =
    basket?.materialBasketItem?.map((item) => ({
      ...item,
      itemEditingAllowed: true,
      quantityEditingAllowed: true,
    })) ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const isAddingItemsToBasket =
    priceListItemResult.isLoading || warehouseArticleItemResult.isLoading;

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isCheckingOutBasket ||
    isDiscardingBasket ||
    isChangingQuantity ||
    isEditingMaterialitem ||
    isDeletingBasketItems;

  const {setBasketItemQuantity, hasInvalidBasketItems, hasUnsettledQuantity} =
    useBasketItemsQuantityState(basketItems);

  const handleAddWarehouseArticle = async (arg: PostBasketMaterialItemWarehouseArticleApiApiArg) =>
    await postBasketMaterialItemWarehouseArticle(arg).unwrap().catch(handleApiError);

  const handleAddPriceList = async (arg: PostBasketMaterialItemPriceListApiApiArg) =>
    await postBasketMaterialItemPriceList(arg).unwrap().catch(handleApiError);

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setBasketItemQuantity(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchBasketItemQuantity({
      basketItemId: itemId,
      body: {
        quantity: quantity.newQuantity,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEdit = (item: MaterialBasketItemEditingDetails) =>
    openDialog(
      <EditMaterialItem itemId={item.itemId ?? ''} context="basket" onClose={closeCurrentDialog} />,
      {
        title: `${item.itemName} (${item.itemNumber})`,
      }
    );

  const handleAddMaterial = () =>
    postBasketMaterialItemCheckout({packageId: props.packageId})
      .unwrap()
      .then(props.onAfterSubmit)
      .then(props.onClose)
      .catch(handleApiError);

  const handleCancel = () =>
    deleteBasketMaterialItems({packageId: props.packageId})
      .unwrap()
      .then(props.onClose)
      .catch(handleApiError);

  const actions = buildArray<ButtonProps>()
    .add({
      isLoading: isDiscardingBasket,
      isDisabled: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleCancel,
      'data-testid': suffixTestId('cancel', props),
    })
    .add({
      isLoading: isCheckingOutBasket,
      isDisabled: isBasketEmpty || hasPendingRequest || hasUnsettledQuantity,
      title: i18n.t('general.actions.add'),
      onClick: handleAddMaterial,
      'data-testid': suffixTestId('add', props),
    });

  const handleDelete = (itemsIds: string[]) => {
    const ids = isArray(itemsIds) ? itemsIds : [itemsIds];

    const requestBody = {basketItemIds: ids};

    return deleteBasketItems({body: requestBody}).unwrap().catch(handleApiError);
  };

  return (
    <FullScreenModal
      headline={i18n.t('entity.workshopServicePackages.labels.addMaterial')}
      actions={actions}
    >
      <Box padding={4} height="100%">
        <VStack spacing={4} height="100%">
          <Box maxHeight="100%" flex={1}>
            <HStack spacing={4} height="100%">
              <Box flex={5}>
                <DataStatus
                  isLoading={isAvailableWarehousesLoading}
                  isError={isAvailableWarehousesError}
                >
                  <MaterialItemsListCard
                    availableWarehouses={availableWarehouses ?? []}
                    packageId={props.packageId}
                    onWarehouseArticleAdd={handleAddWarehouseArticle}
                    onPriceListAdd={handleAddPriceList}
                  />
                </DataStatus>
              </Box>
              <Box flex={1} minWidth={86}>
                <AfterSalesMaterialBasket<ExtendedMateriallBasketItemType>
                  basket={{
                    items: basketItems,
                    totalPrice: null,
                    isLoading: isBasketLoading,
                    hasError: isBasketError,
                    hasPendingRequest,
                    hasInvalidItems: hasInvalidBasketItems,
                    isPriceHidden: true,
                  }}
                  mechanic={{isMechanicHidden: true}}
                  onQuantityChange={handleQuantityChange}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  data-testid={suffixTestId('basket', props)}
                />
              </Box>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </FullScreenModal>
  );
}
